import { Link } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import ElfsightWidget from '../components/about/ElfsightWidget';
import SEOHead from '../components/SEOHead';
import { essays, Essay } from '../data/essays';
import { ArrowRight } from 'lucide-react';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';
import heroBg from '@/assets/essays-hero.jpg';
import essayCertainty from '@/assets/essay-certainty-is-now-free.jpg';
import essayOversight from '@/assets/essay-oversight-degrades.jpg';
import essayTechLed from '@/assets/essay-tech-led-human-powered.jpg';
import essaySpaceBetween from '@/assets/essay-the-space-between.jpg';
import essayHumanLoop from '@/assets/essay-the-human-loop.jpg';

const Essays = () => {
  const lead = useScrollReveal<HTMLDivElement>();

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "ParadoxEdge Essays",
    "url": "https://www.paradoxedge.com/essays",
    "author": { "@type": "Person", "name": "Kent Frazier" },
    "blogPost": essays.map((e) => ({
      "@type": "BlogPosting",
      "headline": e.title,
      "description": e.subtitle,
      "datePublished": e.date,
      "author": { "@type": "Person", "name": e.author },
      "url": `https://www.paradoxedge.com/essays/${e.slug}`,
    })),
  };

  const [featured, ...rest] = essays;

  const essayImages: Record<string, string> = {
    'certainty-is-now-free': essayCertainty,
    'oversight-degrades-the-overseer': essayOversight,
    'tech-led-human-powered': essayTechLed,
    'the-space-between': essaySpaceBetween,
    'the-human-loop': essayHumanLoop,
  };

  const getImage = (slug: string) => essayImages[slug] ?? essayCertainty;

  return (
    <>
      <SEOHead
        title="Essays"
        description="Essays from Kent Frazier on leadership, development, and the conditions in which human beings grow."
        canonicalUrl="/essays"
        jsonLd={jsonLd}
      />
      <div className="min-h-screen bg-background flex flex-col">
        <Navigation />
        <ElfsightWidget />
        <main className="flex-1">
          {/* Hero */}
          <section className="relative overflow-hidden bg-foreground">
            <div className="absolute inset-0">
              <img
                src={heroBg}
                alt=""
                className="w-full h-full object-cover object-center opacity-25"
                loading="eager"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-b from-foreground/85 via-foreground/80 to-foreground" />
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  'radial-gradient(ellipse at 12% 90%, hsl(var(--primary) / 0.25) 0%, transparent 50%)',
              }}
              aria-hidden="true"
            />

            <div className="container-mobile relative z-10 max-w-5xl mx-auto pt-32 pb-24 sm:pt-40 sm:pb-32">
              <div
                ref={lead.ref}
                className={`max-w-4xl transition-all duration-1000 ${
                  lead.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
                }`}
              >
                <div className="flex items-center gap-4 mb-8">
                  <span className="h-px w-12 bg-primary" aria-hidden="true" />
                  <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs">
                    Essays
                  </p>
                </div>
                <h1
                  className="text-background text-[2.75rem] sm:text-6xl lg:text-[5.5rem] leading-[1.02] mb-8 sm:mb-10"
                  style={{ textWrap: 'balance' }}
                >
                  Writing on leadership and{' '}
                  <span className="text-primary italic">technology.</span>
                </h1>
                <p className="text-background/80 text-lg sm:text-xl leading-relaxed max-w-3xl">
                  On the work of leadership, the practice of development, and the conditions in which human beings grow.
                </p>
              </div>
            </div>
          </section>

          {/* Featured spread */}
          {featured && (
            <section className="py-20 sm:py-24 border-b border-border">
              <div className="container-mobile max-w-7xl mx-auto">
                <article className="group">
                  <Link
                    to={`/essays/${featured.slug}`}
                    className="grid md:grid-cols-12 gap-10 md:gap-14 items-center"
                    onClick={() => window.scrollTo(0, 0)}
                  >
                    <div className="md:col-span-7 relative overflow-hidden bg-muted aspect-[16/10] rounded-lg">
                      <img
                        src={getImage(featured.slug)}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000 ease-out"
                        loading="lazy"
                        width={1024}
                        height={640}
                      />
                      <span className="absolute bottom-5 left-5 px-3 py-1 bg-foreground text-background text-[10px] uppercase tracking-widest font-semibold rounded">
                        Latest
                      </span>
                    </div>
                    <div className="md:col-span-5">
                      <div className="flex flex-wrap items-center gap-3 mb-5 text-[10px] uppercase tracking-widest text-primary font-bold">
                        <span>Featured</span>
                        <span className="w-1.5 h-1.5 rounded-full bg-border" aria-hidden="true" />
                        <span className="text-muted-foreground font-normal">
                          {featured.readingMinutes} min read
                        </span>
                      </div>
                      <h3 className="font-display text-3xl md:text-[2.75rem] leading-[1.1] mb-5 text-foreground group-hover:text-primary transition-colors">
                        {featured.title}
                      </h3>
                      <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                        {featured.subtitle}
                      </p>
                      <time className="text-sm text-muted-foreground block mb-6" dateTime={featured.date}>
                        {featured.dateDisplay}
                      </time>
                      <span className="inline-flex items-center gap-2 text-sm font-semibold text-primary">
                        Read essay
                        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                      </span>
                    </div>
                  </Link>
                </article>
              </div>
            </section>
          )}

          {/* Archive grid */}
          <section className="py-20 sm:py-24">
            <div className="container-mobile max-w-7xl mx-auto">
              <div className="flex items-center gap-4 mb-12">
                <p className="text-primary font-semibold tracking-[0.25em] uppercase text-xs whitespace-nowrap">
                  More essays
                </p>
                <span className="h-px flex-1 bg-border" aria-hidden="true" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-14">
                {rest.map((essay) => (
                  <article key={essay.slug} className="group">
                    <Link
                      to={`/essays/${essay.slug}`}
                      className="block h-full"
                      onClick={() => window.scrollTo(0, 0)}
                    >
                      <div className="aspect-[16/10] bg-muted mb-6 relative overflow-hidden rounded-lg">
                        <img
                          src={getImage(essay.slug)}
                          alt=""
                          className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000 ease-out"
                          loading="lazy"
                          width={1024}
                          height={640}
                        />
                      </div>
                      <span className="text-xs text-muted-foreground block mb-3">
                        {essay.dateDisplay} &middot; {essay.readingMinutes} min read
                      </span>
                      <h4 className="font-display text-2xl leading-snug mb-3 text-foreground group-hover:text-primary transition-colors">
                        {essay.title}
                      </h4>
                      <p className="text-sm text-muted-foreground leading-relaxed mb-5">
                        {essay.subtitle}
                      </p>
                      <span className="inline-flex items-center gap-2 text-sm font-semibold text-primary">
                        Read essay
                        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                      </span>
                    </Link>
                  </article>
                ))}
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default Essays;
